import { arenaBoundsFor, FIXED_DT } from './balance'
import type { Boss, Player } from './types'

// Trial only: game.ts accepts this switch exclusively inside its DEV gate.
let enabled = false
export function setBossNeutralMovementTrial(value: boolean): void { enabled = value }
export type NeutralMovement = {
  action: 'wait' | 'approach' | 'retreat'
  elapsedMs: number
  durationMs: number
  dir: 1 | -1
  speed: number
  targetX: number
  repositioned: boolean
}
const PROFILES: Record<string, { holdMs: number; crampedPx: number; retreatPx: number }> = {
  'crowned-king-awoken': { holdMs: 220, crampedPx: 100, retreatPx: 40 },
  'vigil-knight': { holdMs: 280, crampedPx: 65, retreatPx: 50 },
}
export function usesNeutralMovementTrial(boss: Boss): boolean {
  return enabled && !!PROFILES[boss.id]
}
export function clearNeutralMovement(boss: Boss): Boss {
  if (!boss.neutralAction) return boss
  const { neutralAction: _old, ...rest } = boss
  return rest
}
export function prepareNeutralMovement(boss: Boss): Boss {
  return usesNeutralMovementTrial(boss) && boss.state === 'idle'
    ? boss : clearNeutralMovement(boss)
}

// Called only during idle, after retaliation / authored standoff / forced movement.
// Existing attack timing and selection remain downstream; allowAttack only gates
// voluntary attacks while a movement action is unfinished. No damage or push-out.
export function updateNeutralMovement(
  boss: Boss, player: Player, commitRange: number, walkSpeed: number, attackGapMs: number,
): { boss: Boss; allowAttack: boolean } {
  const profile = PROFILES[boss.id]!
  const dtMs = FIXED_DT * 1000
  const walls = arenaBoundsFor(boss.id)
  const toward: 1 | -1 = player.x < boss.x ? -1 : 1
  const distance = Math.abs(player.x - boss.x)
  const wait = (durationMs: number, repositioned: boolean): NeutralMovement => ({
    action: 'wait', elapsedMs: 0, durationMs, dir: toward, speed: 0,
    targetX: boss.x, repositioned,
  })
  let movement = boss.neutralAction ?? wait(profile.holdMs, false)
  movement = { ...movement, elapsedMs: movement.elapsedMs + dtMs }
  const finish = () => ({ boss: { ...boss, neutralAction: wait(160, movement.repositioned) }, allowAttack: false })

  if (movement.action === 'wait') {
    // Do not shadow the player during the existing gap between attacks either.
    if (movement.elapsedMs < movement.durationMs || boss.stateElapsed * 1000 < attackGapMs) {
      return { boss: { ...boss, neutralAction: movement }, allowAttack: false }
    }
    if (distance > commitRange) {
      movement = { ...movement, action: 'approach', elapsedMs: 0, durationMs: 900,
        dir: toward, speed: 0 }
    } else if (distance < profile.crampedPx && !movement.repositioned) {
      // One short authored reset, not an always-on preferred-distance controller.
      const targetX = Math.max(walls.leftWall, Math.min(walls.rightWall,
        boss.x - toward * profile.retreatPx))
      movement = { ...movement, action: 'retreat', elapsedMs: 0, durationMs: 450,
        dir: toward, targetX, speed: 0, repositioned: true }
      if (Math.abs(targetX - boss.x) < 1) return finish()
    } else {
      return { boss: { ...boss, neutralAction: movement }, allowAttack: true }
    }
  }

  // A flank ends this movement; it never reverses its direction on the same tick.
  if ((player.x - boss.x) * movement.dir <= 0 || movement.elapsedMs >= movement.durationMs) return finish()
  const remaining = movement.action === 'approach'
    ? (player.x - boss.x) * movement.dir - Math.max(0, commitRange - 10)
    : (boss.x - movement.targetX) * movement.dir
  if (remaining <= 0.01) return finish()
  const maxSpeed = movement.action === 'approach' ? walkSpeed : Math.min(140, walkSpeed)
  // Acceleration and stopping distance keep short moves from snapping into full speed.
  const acceleration = 1200
  const speed = Math.min(maxSpeed, movement.speed + acceleration * FIXED_DT,
    Math.sqrt(2 * acceleration * remaining))
  const sign = movement.action === 'approach' ? movement.dir : -movement.dir
  const x = Math.max(walls.leftWall, Math.min(walls.rightWall,
    boss.x + sign * Math.min(remaining, speed * FIXED_DT)))
  if (Math.abs(x - boss.x) < 0.001) return finish()
  return { boss: { ...boss, x, neutralAction: { ...movement, speed } }, allowAttack: false }
}
